// Case Detail Page
import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { POLICY_STATUS_LABELS, POLICY_STATUS_DESCRIPTIONS } from '../lib/policyStatus';
import { PolicyPanel } from '../components/PolicyPanel';
import {
  ArrowLeft, Pencil, Loader2, AlertTriangle, Eye, EyeOff, Upload,
  FileText, MessageSquare, History, ShieldCheck, Plus, BellRing, Download, FileCheck, Check, Trash2, X, IdCard, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CommunicationPanel } from '../components/CommunicationPanel';
import { EditClientDialog } from '../components/EditClientDialog';
import { RenewCaseDialog } from '../components/RenewCaseDialog';
import { EnrollmentTypeBadge } from '../components/EnrollmentTypeBadge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const statusColors = {
  'New Submission': 'bg-sky-100 text-sky-700',
  'Under Review': 'bg-amber-100 text-amber-700',
  'Missing Information': 'bg-orange-100 text-orange-700',
  'Documents Due': 'bg-orange-100 text-orange-700',
  'Documents Received': 'bg-teal-100 text-teal-700',
  'Pending Client Contact': 'bg-purple-100 text-purple-700',
  'Pending Marketplace Application': 'bg-indigo-100 text-indigo-700',
  'Application Submitted': 'bg-blue-100 text-blue-700',
  'Enrolled': 'bg-green-100 text-green-700',
  'Not Eligible': 'bg-slate-100 text-slate-700',
  'Closed': 'bg-slate-100 text-slate-700',
  'Pending Renewal Review': 'bg-purple-100 text-purple-700',
};

const consentColors = {
  'Consent Received': 'bg-green-100 text-green-700',
  'Consent Pending': 'bg-amber-100 text-amber-700',
  'Consent Requested': 'bg-sky-100 text-sky-700',
  'Consent Declined': 'bg-red-100 text-red-700',
  'Consent Not Required': 'bg-slate-100 text-slate-700',
};

function InfoRow({ label, value }) {
  return (
    <div className="py-2 border-b border-slate-100 last:border-0">
      <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value || '—'}</p>
    </div>
  );
}

export function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const [caseData, setCaseData] = useState(null);
  const [yearVersions, setYearVersions] = useState([]);
  const [constants, setConstants] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revealSsn, setRevealSsn] = useState(false);

  const [newNote, setNewNote] = useState('');
  const [newDocCategory, setNewDocCategory] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [notifyReason, setNotifyReason] = useState('');
  const [policy, setPolicy] = useState({});
  const [consent, setConsent] = useState({});
  const [docToDelete, setDocToDelete] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managerValue, setManagerValue] = useState('');
  const [premiumValue, setPremiumValue] = useState('Pending');
  const [clientAgent, setClientAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentValue, setAgentValue] = useState('none');
  const [agentSaving, setAgentSaving] = useState(false);
  const isCalyvraAdmin = user?.role === 'admin';

  const [demoOpen, setDemoOpen] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(true);

  const canManageClients = hasPermission('clients.manage');

  const openDemographics = async () => {
    setDemoOpen(true);
    setDemoLoading(true);
    try {
      setDemoData(await portalApi.getCaseDemographics(id));
    } catch (err) {
      toast.error(err.message || 'Error loading demographics');
    } finally {
      setDemoLoading(false);
    }
  };

  const openEditClient = () => { setDemoOpen(false); setEditClientOpen(true); };

  const canAssignManager = user?.role === 'admin' || user?.role === 'ls_admin';

  const loadCase = useCallback(async (reveal = false) => {
    const data = await portalApi.getCase(id, reveal);
    setCaseData(data);
    setStatusValue(data.status);
    setPolicy(data.policy || {});
  }, [id]);

  const handleEnrollmentOverride = async (val) => {
    try {
      await portalApi.setEnrollmentType(id, val);
      await loadCase(false);
      toast.success(val === 'auto' ? 'Enrollment type set to automatic' : `Enrollment type set to ${val}`);
    } catch (e) {
      toast.error(e.message || 'Could not update enrollment type');
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, consts, docs, n, a] = await Promise.all([
        portalApi.getCase(id, false),
        portalApi.getConstants(),
        portalApi.getDocuments(id).catch(() => []),
        portalApi.getNotes(id).catch(() => []),
        portalApi.getCaseAudit(id).catch(() => []),
      ]);
      setCaseData(c);
      portalApi.getCaseYearVersions(id).then((r) => setYearVersions(r.versions || [])).catch(() => setYearVersions([]));
      setStatusValue(c.status);
      setManagerValue(c.case_manager_id || 'unassigned');
      setPolicy(c.policy || {});
      setPremiumValue(c.premium_support_status || 'Pending');
      if (c.workspace_key && c.workspace_key !== 'latinos_salud' && c.client_id) {
        portalApi.getClient(c.client_id).then((res) => setClientAgent(res.client?.primary_agent_name || null)).catch(() => {});
      }
      // Calyvra Admin: load the client's current primary agent + the assignable agent
      // list so the agent can be assigned directly from the case (any workspace).
      if (user?.role === 'admin' && c.client_id) {
        portalApi.getClient(c.client_id)
          .then((res) => {
            setClientAgent(res.client?.primary_agent_name || null);
            setAgentValue(res.client?.primary_agent_id || 'none');
          }).catch(() => {});
        portalApi.getAgents().then(setAgents).catch(() => {});
      }
      setConsent({
        consent_status: c.consent_status || 'Consent Pending',
        consent_date: c.consent_date || '',
        consent_method: c.consent_method || '',
        consent_collected_by: c.consent_collected_by || '',
        consent_notes: c.consent_notes || '',
      });
      setConstants(consts);
      setDocuments(docs);
      setNotes(n);
      setAudit(a);
      if (user?.role === 'admin' || user?.role === 'ls_admin') {
        portalApi.getAssignableUsers().then(setManagers).catch(() => {});
      }
    } catch (err) {
      toast.error(err.message || 'Error loading case');
    } finally {
      setLoading(false);
    }
  }, [id, user]);

  useEffect(() => {
    document.title = 'Case Detail · Latinos Salud';
    loadAll();
  }, [loadAll]);

  const toggleSsn = async () => {
    if (!revealSsn) {
      await loadCase(true);
      setRevealSsn(true);
    } else {
      await loadCase(false);
      setRevealSsn(false);
    }
  };

  const handleStatusChange = async () => {
    try {
      await portalApi.changeCaseStatus(id, statusValue);
      toast.success('Status updated');
      await loadAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleChangeManager = async () => {
    try {
      await portalApi.updateCase(id, { case_manager_id: managerValue === 'unassigned' ? '' : managerValue });
      toast.success('Case manager updated');
      await loadAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAssignAgent = async () => {
    if (!caseData?.client_id) { toast.error('This case has no linked client to assign.'); return; }
    setAgentSaving(true);
    try {
      await portalApi.updateClient(caseData.client_id, { primary_agent_id: agentValue === 'none' ? '' : agentValue });
      toast.success(agentValue === 'none' ? 'Agent unassigned' : 'Agent assigned — they have been notified');
      const res = await portalApi.getClient(caseData.client_id);
      setClientAgent(res.client?.primary_agent_name || null);
      setAgentValue(res.client?.primary_agent_id || 'none');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAgentSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await portalApi.addNote(id, newNote);
      setNewNote('');
      toast.success('Note added');
      const [n, a] = await Promise.all([portalApi.getNotes(id), portalApi.getCaseAudit(id)]);
      setNotes(n);
      setAudit(a);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddDocument = async () => {
    if (!newDocCategory) return;
    try {
      await portalApi.createDocument(id, { category: newDocCategory, status: 'Due' });
      setNewDocCategory('');
      toast.success('Document request added');
      setDocuments(await portalApi.getDocuments(id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFileUpload = async (docId, file) => {
    if (!file) return;
    try {
      await portalApi.uploadDocumentFile(id, docId, file);
      toast.success('File uploaded');
      setDocuments(await portalApi.getDocuments(id));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const confirmDeleteDoc = async () => {
    if (!docToDelete) return;
    try {
      if (docToDelete.fileId) {
        await portalApi.deleteDocumentFile(id, docToDelete.docId, docToDelete.fileId);
        toast.success('File deleted');
      } else {
        await portalApi.deleteDocument(id, docToDelete.docId);
        toast.success('Document deleted');
      }
      setDocToDelete(null);
      setDocuments(await portalApi.getDocuments(id));
    } catch (err) {
      toast.error(err.message);
      setDocToDelete(null);
    }
  };

  const canDeleteDocs = user?.role === 'admin';

  const handleSaveConsent = async () => {
    try {
      await portalApi.updateCase(id, consent);
      toast.success('Consent updated');
      await loadAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePremiumChange = async (value) => {
    setPremiumValue(value);
    try {
      await portalApi.updateCase(id, { premium_support_status: value });
      toast.success('Premium Support Status updated');
      setCaseData((prev) => ({ ...prev, premium_support_status: value }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleNotifyManager = async () => {
    if (!notifyReason.trim()) return;
    try {
      await portalApi.notifyManager(id, notifyReason);
      setNotifyReason('');
      toast.success('Case manager notified');
      await loadAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleResolveAttention = async () => {
    try {
      await portalApi.resolveAttention(id);
      toast.success('Attention alert cleared');
      await loadAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const canResolveAttention =
    user?.role === 'admin' ||
    user?.role === 'ls_admin' ||
    (caseData && caseData.case_manager_id === user?.id);

  if (loading || !caseData) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-detail-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn" className="flex-shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl lg:text-2xl font-bold text-slate-900 break-words">Athena ID: {caseData.athena_id}</h1>
              {caseData.urgent && <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" />Urgent</Badge>}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {yearVersions.length > 1 ? (
                <Select
                  value={id}
                  onValueChange={(v) => { if (v !== id) navigate(`/portal/cases/${v}`); }}
                >
                  <SelectTrigger className="h-8 w-[90px]" data-testid="year-version-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearVersions.map((yv) => (
                      <SelectItem key={yv.id} value={yv.id} data-testid={`year-version-${yv.id}`}>
                        {yv.year || yv.coverage_year_label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-slate-500">{(caseData.coverage_year_label || '').match(/\d{4}/)?.[0] || caseData.coverage_year_label}</span>
              )}
              {caseData.office && <span className="text-slate-500 whitespace-nowrap">· {caseData.office}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap lg:flex-shrink-0">
          <Badge className={statusColors[caseData.status] || 'bg-slate-100 text-slate-700'}>{caseData.status}</Badge>
          <Badge className={consentColors[caseData.consent_status] || 'bg-slate-100 text-slate-700'} data-testid="consent-badge">{caseData.consent_status}</Badge>
          {caseData.enrollment_type === 'NotTracked' ? (
            <span data-testid="enrollment-type-badge"><EnrollmentTypeBadge type="NotTracked" /></span>
          ) : user?.role === 'admin' ? (
            <Select value={caseData.enrollment_type || 'Unverified'} onValueChange={handleEnrollmentOverride}>
              <SelectTrigger className="h-8 w-[150px]" data-testid="enrollment-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="New">NEW</SelectItem>
                <SelectItem value="Renewal">RENEWAL</SelectItem>
                <SelectItem value="Unverified">UNVERIFIED</SelectItem>
                <SelectItem value="auto">Auto (by history)</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <span data-testid="enrollment-type-badge"><EnrollmentTypeBadge type={caseData.enrollment_type} /></span>
          )}
          <Button variant="outline" size="sm" onClick={openDemographics} data-testid="view-demographics-btn">
            <IdCard className="h-4 w-4 mr-2" />Demographics
          </Button>
          {caseData.renewal_status === 'renewed' ? (
            <Badge className="bg-green-100 text-green-700" data-testid="renew-status-renewed">
              <Check className="h-3 w-3 mr-1" />Renewed
            </Badge>
          ) : caseData.renewal_status === 'in_progress' ? (
            hasPermission('agent.producer') ? (
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setRenewOpen(true)} data-testid="renew-case-btn">
                <RefreshCw className="h-4 w-4 mr-2" />Complete Renewal
              </Button>
            ) : (
              <Badge className="bg-amber-100 text-amber-700" data-testid="renew-status-in-progress">
                <RefreshCw className="h-3 w-3 mr-1" />In Progress
              </Badge>
            )
          ) : caseData.renewal_status === 'pending' ? (
            hasPermission('agent.producer') ? (
              <Button variant="outline" size="sm" className="border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => setRenewOpen(true)} data-testid="renew-case-btn">
                <RefreshCw className="h-4 w-4 mr-2" />Renew{caseData.renewal_target_year ? ` for ${caseData.renewal_target_year}` : ''}
              </Button>
            ) : (
              <Badge className="bg-orange-100 text-orange-700" data-testid="renew-status-pending">
                <RefreshCw className="h-3 w-3 mr-1" />Pending Renewal
              </Badge>
            )
          ) : null}
          {hasPermission('cases.edit') && (
            <Button asChild size="sm" data-testid="edit-case-btn">
              <Link to={`/portal/cases/${id}/edit`}><Pencil className="h-4 w-4 mr-2" />Edit</Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setChatOpen((v) => !v)} data-testid="toggle-chat-btn" className="xl:inline-flex">
            <MessageSquare className="h-4 w-4 mr-2" />{chatOpen ? 'Hide chat' : 'Show chat'}
          </Button>
        </div>
      </div>

      <RenewCaseDialog caseItem={caseData} open={renewOpen} onOpenChange={setRenewOpen} onRenewed={() => loadCase(false)} />

      {/* Two-column body: main content + (optional) chat. Header above is full-width,
          so the chat panel can never overlap any header control. */}
      <div className="flex flex-col xl:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0 w-full space-y-6">
      {caseData.needs_attention && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start justify-between gap-4" data-testid="attention-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">This case needs attention</p>
              {caseData.needs_attention_reason && (
                <p className="text-sm text-amber-700 mt-0.5">{caseData.needs_attention_reason}</p>
              )}
            </div>
          </div>
          {canResolveAttention && (
            <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0" onClick={handleResolveAttention} data-testid="resolve-attention-btn">
              <Check className="h-4 w-4 mr-1" /> Clear alert
            </Button>
          )}
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="policy" data-testid="tab-policy">Policy</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3">Basic</h3>
              <InfoRow label="Intake Type" value={caseData.intake_type} />
              {caseData.intake_type === 'Special Enrollment Period' && (
                <>
                  <InfoRow label="SEP Life Event Date" value={caseData.sep_life_event_date} />
                  <InfoRow label="SEP Life Event Notes" value={caseData.sep_life_event_notes} />
                </>
              )}
              <InfoRow label="Program" value={caseData.program_type} />
              <InfoRow label="Case Manager" value={caseData.case_manager_name} />
              <InfoRow label="Language" value={caseData.preferred_language} />
              <InfoRow label="Created By" value={caseData.created_by_name} />
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center justify-between">
                Eligibility
                {hasPermission('cases.reveal_ssn') && (
                  <Button variant="ghost" size="sm" onClick={toggleSsn} data-testid="reveal-ssn-btn">
                    {revealSsn ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                )}
              </h3>
              <InfoRow label="SSN" value={caseData.ssn} />
              <InfoRow label="Immigration Status" value={caseData.immigration_status} />
              <InfoRow label="Marital Status" value={caseData.marital_status} />
              <InfoRow label="Has Dependents" value={caseData.has_dependents ? 'Yes' : 'No'} />
              <InfoRow label="Currently Smokes" value={caseData.currently_smokes ? 'Yes' : 'No'} />
            </div>
          </div>

          {/* Consent editor */}
          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="consent-editor">
            <div className="flex items-center gap-2 mb-4">
              <FileCheck className="h-5 w-5 text-sky-500" />
              <h3 className="font-semibold text-slate-900">Consent</h3>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Consent Status</Label>
                <Select value={consent.consent_status} onValueChange={(v) => setConsent({ ...consent, consent_status: v })} disabled={!hasPermission('cases.edit')}>
                  <SelectTrigger data-testid="consent-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{constants?.consent_statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Consent Date</Label>
                <Input type="date" value={consent.consent_date} onChange={(e) => setConsent({ ...consent, consent_date: e.target.value })} disabled={!hasPermission('cases.edit')} />
              </div>
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={consent.consent_method || 'none'} onValueChange={(v) => setConsent({ ...consent, consent_method: v === 'none' ? '' : v })} disabled={!hasPermission('cases.edit')}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Select...</SelectItem>{constants?.consent_methods?.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={consent.consent_notes} onChange={(e) => setConsent({ ...consent, consent_notes: e.target.value })} disabled={!hasPermission('cases.edit')} />
              </div>
            </div>
            {hasPermission('cases.edit') && (
              <div className="flex justify-end mt-4">
                <Button onClick={handleSaveConsent} data-testid="save-consent-btn">Save Consent</Button>
              </div>
            )}
          </div>

          {/* Status change + manager + notify */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="premium-support-card">
              <h3 className="font-semibold text-slate-900 mb-3">Premium Support Status</h3>
              <Select value={premiumValue} onValueChange={handlePremiumChange} disabled={!hasPermission('cases.edit')}>
                <SelectTrigger data-testid="premium-support-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {constants?.premium_support_statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-2">Updated manually. Not linked to policy status.</p>
            </div>
            {isCalyvraAdmin && caseData.client_id && (
              <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="case-agent-card">
                <h3 className="font-semibold text-slate-900 mb-1">Agent / Producer</h3>
                <p className="text-xs text-slate-500 mb-3">Assign an insurance agent to this client. The agent is notified and the client becomes searchable/visible to them. Agents only see clients assigned to them.</p>
                <p className="text-sm text-slate-700 mb-2">Current: <span className="font-medium" data-testid="case-agent-name">{clientAgent || 'Unassigned'}</span></p>
                <div className="flex gap-3">
                  <Select value={agentValue} onValueChange={setAgentValue}>
                    <SelectTrigger data-testid="case-agent-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleAssignAgent} disabled={agentSaving} data-testid="case-assign-agent-btn">
                    {agentSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
                  </Button>
                </div>
              </div>
            )}
            {hasPermission('cases.change_status') && (
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">Change Status</h3>
                <div className="flex gap-3">
                  <Select value={statusValue} onValueChange={setStatusValue}>
                    <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {constants?.case_statuses?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleStatusChange} data-testid="save-status-btn">Update</Button>
                </div>
              </div>
            )}
            {canAssignManager && (
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">Case Manager</h3>
                <div className="flex gap-3">
                  <Select value={managerValue} onValueChange={setManagerValue}>
                    <SelectTrigger data-testid="manager-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={handleChangeManager} data-testid="save-manager-btn">Assign</Button>
                </div>
              </div>
            )}
            {hasPermission('notify.users') && caseData.case_manager_id && (
              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">Notify Case Manager</h3>
                <div className="flex gap-3">
                  <Input value={notifyReason} onChange={(e) => setNotifyReason(e.target.value)} placeholder="Reason for attention" data-testid="notify-reason-input" />
                  <Button onClick={handleNotifyManager} variant="outline" data-testid="notify-manager-btn"><BellRing className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </div>

          {caseData.notes && (
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Case Notes</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{caseData.notes}</p>
            </div>
          )}
        </TabsContent>

        {/* Policy */}
        <TabsContent value="policy" className="mt-6">
          <PolicyPanel
            caseId={id}
            athenaId={caseData.athena_id}
            canEdit={hasPermission('cases.add_policy')}
            constants={constants}
            workspaceKey={caseData.workspace_key}
          />
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents" className="mt-6 space-y-6">
          {hasPermission('cases.edit') && (
            <div className="bg-white rounded-xl p-4 border border-slate-200 flex items-end gap-3">
              <div className="flex-1 space-y-2">
                <Label>Add Document Request</Label>
                <Select value={newDocCategory} onValueChange={setNewDocCategory}>
                  <SelectTrigger data-testid="doc-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{constants?.document_categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddDocument} data-testid="add-doc-btn"><Plus className="h-4 w-4 mr-2" />Add</Button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {documents.length === 0 ? (
              <div className="p-12 text-center text-slate-400"><FileText className="h-10 w-10 mx-auto mb-3" />No documents</div>
            ) : documents.map(doc => (
              <div key={doc.id} className="p-4 flex items-center justify-between" data-testid={`doc-${doc.id}`}>
                <div>
                  <p className="font-medium text-slate-800">{doc.category}</p>
                  <Badge className={doc.status === 'Received' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{doc.status}</Badge>
                  {doc.files?.map(f => (
                    <div key={f.id} className="flex items-center gap-2 mt-1">
                      <a href={portalApi.getDocumentFileUrl(id, doc.id, f.id)} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline">
                        <Download className="h-3 w-3 inline mr-1" />{f.filename}
                      </a>
                      {canDeleteDocs && (
                        <button type="button" onClick={() => setDocToDelete({ docId: doc.id, fileId: f.id, label: f.filename })} className="text-slate-400 hover:text-red-600" data-testid={`delete-file-${f.id}`} title="Delete file">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  {hasPermission('cases.upload') && (
                    <label className="cursor-pointer">
                      <input type="file" className="hidden" onChange={(e) => handleFileUpload(doc.id, e.target.files[0])} data-testid={`upload-input-${doc.id}`} />
                      <span className="inline-flex items-center text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50"><Upload className="h-4 w-4 mr-2" />Upload</span>
                    </label>
                  )}
                  {canDeleteDocs && (
                    <Button variant="ghost" size="sm" onClick={() => setDocToDelete({ docId: doc.id, label: doc.category })} data-testid={`delete-doc-${doc.id}`} title="Delete document">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Notes tab removed — case chat now lives in the Communication Panel */}

        {/* Audit */}
        <TabsContent value="audit" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {audit.length === 0 ? (
              <div className="p-12 text-center text-slate-400"><History className="h-10 w-10 mx-auto mb-3" />No audit entries</div>
            ) : audit.map(log => (
              <div key={log.id} className="p-4 flex items-start gap-3">
                <History className="h-4 w-4 text-slate-400 mt-1" />
                <div>
                  <p className="text-sm text-slate-800"><span className="font-medium">{log.action}</span> by {log.user_name}</p>
                  <p className="text-xs text-slate-400">{new Date(log.timestamp).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-2xl" data-testid="demographics-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><IdCard className="h-5 w-5 text-sky-500" />Client demographics</DialogTitle>
          </DialogHeader>
          {demoLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
          ) : !demoData?.has_demographics ? (
            <div className="py-8 text-center" data-testid="demographics-empty">
              <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="text-slate-700 font-medium">No demographics</p>
              <p className="text-sm text-slate-500 mt-1">This client {demoData?.calyvra_client_id ? `(${demoData.calyvra_client_id})` : ''} doesn't have an address or demographics on file yet.</p>
              {canManageClients && demoData?.client_id && (
                <Button className="mt-4" onClick={openEditClient} data-testid="demographics-add-btn">
                  <Pencil className="h-4 w-4 mr-2" />Add demographics
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                {demoData?.calyvra_client_id} · Athena {demoData?.athena_id || '—'}
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0">
                <InfoRow label="Name" value={[demoData.demographics.first_name, demoData.demographics.last_name].filter(Boolean).join(' ')} />
                <InfoRow label="Date of birth" value={demoData.demographics.date_of_birth} />
                <InfoRow label="Sex" value={demoData.demographics.sex} />
                <InfoRow label="Preferred language" value={demoData.demographics.preferred_language} />
                <InfoRow label="Email" value={demoData.demographics.email} />
                <InfoRow label="Phone" value={demoData.demographics.phone} />
                <div className="sm:col-span-2"><InfoRow label="Address" value={demoData.address} /></div>
                <InfoRow label="County" value={demoData.demographics.county} />
                <InfoRow label="Marital status" value={demoData.demographics.marital_status} />
                <InfoRow label="Immigration status" value={demoData.demographics.immigration_status} />
                <InfoRow label="Occupation" value={demoData.demographics.occupation} />
                <InfoRow label="SSN" value={demoData.demographics.ssn} />
              </div>
              {demoData?.client_id && (
                <div className="flex justify-end gap-2 pt-2">
                  {canManageClients && (
                    <Button variant="outline" onClick={openEditClient} data-testid="demographics-edit-btn">
                      <Pencil className="h-4 w-4 mr-2" />Edit demographics
                    </Button>
                  )}
                  {hasPermission('clients.view') && (
                    <Button variant="outline" asChild data-testid="demographics-open-client">
                      <Link to={`/portal/clients/${demoData.client_id}`}>Open full profile</Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editClientOpen && demoData?.client_id && (
        <EditClientDialog
          open={editClientOpen}
          onOpenChange={setEditClientOpen}
          client={{ id: demoData.client_id, source: '', demographics: demoData.demographics || {} }}
          constants={constants}
          onSaved={openDemographics}
        />
      )}

      <AlertDialog open={Boolean(docToDelete)} onOpenChange={(open) => !open && setDocToDelete(null)}>        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{docToDelete?.fileId ? 'Delete file?' : 'Delete document?'}</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{docToDelete?.label}</strong>{docToDelete?.fileId ? '' : ' and any uploaded files'}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDoc} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-doc-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
      <div className={chatOpen ? 'w-full xl:w-[400px] xl:flex-shrink-0' : 'hidden'}>
        <CommunicationPanel caseId={id} athenaId={caseData.athena_id} caseManagerId={caseData.case_manager_id} />
      </div>
      </div>
    </div>
  );
}

export default CaseDetailPage;
